import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, X, Edit3, Trash2, Eye, Loader2, FileText, Upload, Film, Image, Bell, Heart, MessageSquare, Shield, LogOut, Star } from 'lucide-react';
import { api, getErrorMessage } from '../services/api';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';
import { useAuth } from '../contexts/AuthContext';
import type { PromotionFile, PromotionFileCreate, PromotionViewLog } from '../types/api';
import styles from './PromotionManage.module.css';

const CATEGORIES = ['基材类','油墨涂料类','印刷工艺类','功能类','智能化类','绿色可持续类'];

function getFileIcon(t: string) {
  t = t.toLowerCase();
  if (['mp4','webm','mov','avi'].includes(t)) return <Film size={16} />;
  if (['jpg','jpeg','png','gif','webp'].includes(t)) return <Image size={16} />;
  return <FileText size={16} />;
}

function formatSize(s?: number|null) { if (!s) return ''; return s<1024*1024 ? `${(s/1024).toFixed(1)}KB` : `${(s/1024/1024).toFixed(1)}MB`; }

export default function PromotionManage() {
  const { currentFunctionRole } = useAuth();
  const { showToast } = useToast();
  const confirmDialog = useConfirm();
  const isVideoOwner = currentFunctionRole === '供应商' || currentFunctionRole === '研发';

  let user: any = {};
  try { user = JSON.parse(localStorage.getItem('user') || '{}'); } catch {}

  const [files, setFiles] = useState<PromotionFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title:'', description:'', level:1, category:'' });
  const [uploadFile, setUploadFile] = useState<File|null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState<PromotionFile|null>(null);
  const [editForm, setEditForm] = useState({ title:'', description:'', level:1, category:'' });
  const [editFile, setEditFile] = useState<File|null>(null);
  const [showMobileToolbar, setShowMobileToolbar] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [logTitle, setLogTitle] = useState('');
  const [viewLogs, setViewLogs] = useState<PromotionViewLog[]>([]);
  const [showApproval, setShowApproval] = useState(false);
  const [approvalFileId, setApprovalFileId] = useState(0);
  const [approvalLogs, setApprovalLogs] = useState<any[]>([]);
  const [approvalTitle, setApprovalTitle] = useState('');
  const [approvalStatus, setApprovalStatus] = useState(0);
  const [showUploadCategory, setShowUploadCategory] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('');
  const [showMsg, setShowMsg] = useState(false);
  const [messages, setMessages] = useState<any[]>([]);
  const [msgCount, setMsgCount] = useState(0);
  const msgRef = useRef<HTMLDivElement>(null);
  const [likeCounts, setLikeCounts] = useState<Record<number,number>>({});
  const [favCounts, setFavCounts] = useState<Record<number,number>>({});
  const [commentCounts, setCommentCounts] = useState<Record<number,number>>({});
  const [viewCounts, setViewCounts] = useState<Record<number,number>>({});
  const [cardComments, setCardComments] = useState<Record<number,any[]>>({});
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentModalTitle, setCommentModalTitle] = useState('');
  const [replyTo, setReplyTo] = useState<number|null>(null);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [commentModalFileId, setCommentModalFileId] = useState(0);
  const [previewFile, setPreviewFile] = useState<PromotionFile|null>(null);
  const [showLikeDetail, setShowLikeDetail] = useState(false);
  const [showFavDetail, setShowFavDetail] = useState(false);
  const [showCommentDetail, setShowCommentDetail] = useState(false);
  const [detailTitle, setDetailTitle] = useState('');
  const [detailLikes, setDetailLikes] = useState<any[]>([]);
  const [detailComments, setDetailComments] = useState<any[]>([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getPromotionFiles({ page:1, page_size:100 });
      const data = res.data || [];
      setFiles(data);
      // 所有计数由后端一次性返回（浏览/评论/点赞/收藏）
      const vc: Record<number,number> = {}, cc: Record<number,number> = {}, lc: Record<number,number> = {}, fc: Record<number,number> = {};
      data.forEach((f: any) => { vc[f.file_id]=f.view_count||0; cc[f.file_id]=f.comment_count||0; lc[f.file_id]=f.like_count||0; fc[f.file_id]=f.fav_count||0; });
      setViewCounts(vc); setCommentCounts(cc); setLikeCounts(lc); setFavCounts(fc);
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (!isVideoOwner) return;
    (async () => {
      try { const res = await api.request<any[]>('/promotions/messages'); setMessages(res.data||[]); setMsgCount((res.data||[]).length); }
      catch {}
    })();
  }, [isVideoOwner]);

  const filtered = files.filter(f => {
    const t = f.title || f.file_name;
    if (searchQuery && !t.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (categoryFilter && f.category !== categoryFilter) return false;
    return true;
  });

  const refreshLogsAfterAction = (newStatus: number) => {
    setApprovalStatus(newStatus);
    api.request<any[]>('/promotions/files/' + approvalFileId + '/approval-logs')
      .then(r => setApprovalLogs(r.data || []))
      .catch(() => {});
  };

  const handleSubmitApproval = async () => {
    const ok = await confirmDialog.confirm({ title: '提交审核', message: '确认提交审核？提交后将由采购进行初审。', confirmText: '确认提交' });
    if (!ok) return;
    try {
      await api.request('/promotions/files/' + approvalFileId + '/submit', { method: 'POST' });
      showToast('success', '已提交审核');
      refreshLogsAfterAction(1);  // 提交后状态为采购审核中
      loadData();
    } catch (err: any) { showToast('error', getErrorMessage(err)); }
  };

  const handleWithdraw = async () => {
    const ok = await confirmDialog.confirm({ title: '撤回审核', message: '确认撤回审核？', confirmText: '确认撤回' });
    if (!ok) return;
    try {
      await api.request('/promotions/files/' + approvalFileId + '/withdraw', { method: 'POST' });
      showToast('success', '已撤回');
      refreshLogsAfterAction(0);  // 撤回后回到待提交
      loadData();
    } catch (err: any) { showToast('error', getErrorMessage(err)); }
  };

  const handleDelete = async (f: PromotionFile) => {
    const ok = await confirmDialog.confirm({ title: '确认删除', message: '删除 ' + (f.title || f.file_name) + ' ？', confirmText: '删除', danger: true });
    if (!ok) return;
    try {
      await api.deletePromotionFile(f.file_id);
      showToast('success', '已删除');
      loadData();
    } catch (err) { showToast('error', getErrorMessage(err)); }
  };

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // 组件卸载时清理轮询
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);
  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await api.getPromotionFiles({ page: 1, page_size: 100 });
        const data = res.data || [];
        setFiles(data);
        const hasProcessing = data.some((f: any) => f.transcoded === false);
        if (!hasProcessing || attempts >= 15) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        }
      } catch { /* 忽略轮询错误 */ }
    }, 2000);
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadForm.title.trim()) { showToast('warning', '请填写标题并选择文件'); return; }
    setUploading(true);
    try {
      await api.uploadPromotionFile(uploadFile, uploadForm as PromotionFileCreate);
      showToast('success', '发布成功');
      setShowUpload(false);
      setUploadFile(null);
      setUploadForm({ title:'', description:'', level:1, category:'' });
      loadData();
      startPolling();
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setUploading(false); }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    if (editing.approval_status === 4) {
      const ok = await confirmDialog.confirm({
        title: '编辑已上传视频',
        message: '该视频已发布，编辑后将变为「修改中」状态，需要重新送审才能在客户端展示。确认继续？',
        confirmText: '确认修改',
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      if (editFile) {
        // 发布新文件替换
        await api.uploadPromotionFile(editFile, { title: editForm.title, description: editForm.description, level: editForm.level, category: editForm.category } as PromotionFileCreate);
        await api.deletePromotionFile(editing.file_id);
      } else {
        const data = { ...editForm } as any;
        if (editing.approval_status === 4) data.approval_status = 0;
        await api.updatePromotionFile(editing.file_id, data);
      }
      showToast('success', editing.approval_status === 4 ? '已保存，请重新送审' : '已保存');
      setShowEdit(false);
      setEditFile(null);
      loadData();
      if (editFile) startPolling();
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const openApproval = (f: PromotionFile) => {
    setApprovalFileId(f.file_id);
    setApprovalTitle(f.title || f.file_name);
    setApprovalStatus(f.approval_status);
    setApprovalLogs([]);
    setShowApproval(true);
    api.request<any[]>('/promotions/files/' + f.file_id + '/approval-logs')
      .then(r => setApprovalLogs(r.data || []))
      .catch(() => {});
  };

  const openDetail = (f: PromotionFile, type: 'like'|'fav'|'comment') => {
    setDetailTitle(f.title || f.file_name);
    setDetailLikes([]);
    setDetailComments([]);
    if (type==='like') {
      setShowLikeDetail(true);
      api.request<any>('/promotions/invitee/files/'+f.file_id+'/like')
        .then(r => setDetailLikes([r.data])).catch(()=>{});
    } else if (type==='fav') {
      setShowFavDetail(true);
    } else {
      setShowCommentDetail(true);
      api.request<any[]>('/promotions/invitee/files/'+f.file_id+'/comments')
        .then(r => setDetailComments(r.data||[])).catch(()=>{});
    }
  };

  const handleReply = async (cid: number, fileId: number) => {
    if (!replyText.trim()) return;
    setReplySending(true);
    try {
      await api.request(`/promotions/files/${fileId}/comments/${cid}/reply`, { method:'PUT', body:JSON.stringify({reply:replyText}) });
      showToast('success', '回复成功');
      setReplyText(''); setReplyTo(null);
      const r = await api.request<any[]>('/promotions/invitee/files/' + fileId + '/comments');
      setCardComments(prev => ({...prev, [fileId]: r.data||[]}));
    } catch (e) { showToast('error', getErrorMessage(e)); }
    finally { setReplySending(false); }
  };

  const openLogs = (f: PromotionFile) => {
    setLogTitle(f.title || f.file_name);
    setViewLogs([]);
    setShowLogs(true);
    api.getPromotionViewLogs(f.file_id)
      .then(r => setViewLogs(r.data || []))
      .catch(() => {});
  };

  const formatTime = (d: number) => {
    if (d < 60) return d + '秒';
    if (d < 3600) return Math.floor(d/60) + '分' + (d%60) + '秒';
    return Math.floor(d/3600) + '时' + Math.floor((d%3600)/60) + '分';
  };

  return (
    <div className={styles.container}>

      {/* 移动端筛选按钮 */}
      <div style={{padding:'12px 16px 0',display:'none'}} className={styles.mobileFilterRow}>
        <button onClick={() => setShowMobileToolbar(!showMobileToolbar)} style={{background:'#fff',border:'1px solid var(--color-border)',borderRadius:8,padding:'6px 10px',cursor:'pointer',display:'flex',alignItems:'center',gap:4,fontSize:'.75rem',color:'var(--ink-500)'}}>
          <Search size={14} /> 筛选
        </button>
      </div>
      {/* 工具栏 */}
      {showMobileToolbar && <div onClick={() => setShowMobileToolbar(false)} style={{position:'fixed',inset:0,zIndex:199}} />}
      <div className={`${styles.toolbar} ${showMobileToolbar ? styles.toolbarVisible : ''}`}>
        <div className={styles.searchWrap}><Search size={16} /><input placeholder="搜索..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />{searchQuery && <button onClick={() => setSearchQuery('')}><X size={14} /></button>}</div>
        {!isVideoOwner && (
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={{padding:'6px 12px',borderRadius:8,border:'1px solid var(--ink-200)'}}>
            <option value="">全部分类</option>{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <button onClick={() => { setSearchQuery(''); setCategoryFilter(''); setShowMobileToolbar(false); }} style={{padding:'5px 14px',borderRadius:6,border:'1px solid var(--ink-200)',background:'#fff',fontSize:'.75rem',cursor:'pointer',color:'var(--ink-500)'}} className={styles.mobileOnly}>清除筛选</button>
        <div style={{flex:1}} />
        <button onClick={() => setShowUpload(true)} style={{padding:'8px 16px',borderRadius:8,background:'var(--cinnabar-500)',color:'#fff',border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:6}}>
          <Plus size={16} /> 上传视频
        </button>
      </div>

      <div style={{padding:'0 40px',flex:1,overflow:'auto'}}>
        {loading ? <div style={{textAlign:'center',padding:40}}><Loader2 size={32} className={styles.spin} /></div> :
        filtered.length === 0 ? <div style={{textAlign:'center',padding:40,color:'#9ca3af'}}>暂无视频，点击"上传视频"开始</div> :
        <div className={styles.grid}>
          {filtered.map(f => (
            <motion.div key={f.file_id} className={styles.card} initial={{opacity:0,y:20}} animate={{opacity:1,y:0}}>
              <div className={styles.cardCover} onClick={() => f.transcoded !== false && setPreviewFile(f)}>
                {f.transcoded === false
                  ? <div className={styles.coverEmpty} style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:8}}><Loader2 size={28} className={styles.spin} /><span style={{fontSize:'.75rem',color:'var(--ink-400)'}}>处理中</span></div>
                  : ['mp4','webm','mov','avi'].includes(f.file_type?.toLowerCase()||'')
                  ? <video src={f.preview_url?.replace('/minio/','/internal-minio/')} poster={f.poster_url?.replace('/minio/','/internal-minio/')||undefined} muted loop playsInline className={styles.coverVideo} />
                  : ['jpg','jpeg','png','gif'].includes(f.file_type?.toLowerCase()||'')
                  ? <img src={f.preview_url?.replace('/minio/','/internal-minio/')} className={styles.coverImg} />
                  : <div className={styles.coverEmpty}>{getFileIcon(f.file_type||'')}</div>}
                {!isVideoOwner && (
                <span className={`${styles.statusBadge} ${f.status===1?styles.publicBadge:styles.privateBadge}`}>
                  {f.status===1?'公开':'私有'}{' · Lv.'+f.level}{f.category?' · '+f.category:''}
                </span>
                )}
                {isVideoOwner && (
                  <span className={styles.statusBadge} style={{
                    background:f.approval_status===4?'#dcfce7':f.approval_status===5?'#fee2e2':f.approval_status>0?'#fff7ed':'#eff6ff',
                    color:f.approval_status===4?'#16a34a':f.approval_status===5?'#dc2626':f.approval_status>0?'#ea580c':'#2563eb',
                    fontWeight:600
                  }}>
                    {f.approval_status===4?'✓ 已发布':f.approval_status===5?'✗ 已驳回':f.approval_status>0?'● 审核中':currentFunctionRole==='研发'?'待发布':(f.submitted?'修改中':'● 未送审')}
                  </span>
                )}
              </div>
              <div className={styles.cardBody}>
                <h3>{f.title || f.file_name}</h3>
              </div>
              {/* 互动信息 */}
              <div className={styles.cardActions} style={{borderBottom:'1px solid #f5f0ea'}}>
                <button style={{gap:3,cursor:'default'}} disabled>
                  <Heart size={14} color="#9ca3af"/> {likeCounts[f.file_id] || 0}
                </button>
                <button style={{gap:3,cursor:'default'}} disabled>
                  <Star size={14} color="#9ca3af"/> {favCounts[f.file_id] || 0}
                </button>
                <button onClick={e => {
                    e.stopPropagation();
                    setCommentModalTitle(f.title||f.file_name);
                    setCommentModalFileId(f.file_id);
                    setReplyTo(null); setReplyText('');
                    setShowCommentModal(true);
                    api.request<any[]>('/promotions/invitee/files/' + f.file_id + '/comments')
                      .then(r => setCardComments(prev => ({...prev, [f.file_id]: r.data || []})))
                      .catch(() => {});
                  }} style={{gap:3}}>
                  <MessageSquare size={14} color="#9ca3af"/> {commentCounts[f.file_id] || 0}
                </button>
                <button style={{gap:3,cursor:'default'}} disabled>
                  <Eye size={14} color="#9ca3af"/> {viewCounts[f.file_id] || 0}
                </button>
              </div>
              {/* 操作 */}
              <div className={styles.cardActions}>
                <button onClick={e => { e.stopPropagation(); setEditing(f); setEditForm({title:f.title||f.file_name,description:f.description||'',level:f.level,category:f.category}); setEditFile(null); setShowEdit(true); }}>
                  <Edit3 size={15} /> 编辑
                </button>
                {currentFunctionRole === '供应商' && (
                  <button onClick={e => { e.stopPropagation(); openApproval(f); }}>
                    <Shield size={15} /> 审核
                  </button>
                )}
                {currentFunctionRole === '研发' && (
                  <button onClick={e => { e.stopPropagation(); setApprovalFileId(f.file_id); setApprovalTitle(f.title||f.file_name); setShowUploadCategory(true); }}>
                    <Upload size={15} /> 发布
                  </button>
                )}
                <button className={styles.danger} onClick={e => { e.stopPropagation(); handleDelete(f); }}>
                  <Trash2 size={15} /> 删除
                </button>
              </div>
            </motion.div>
          ))}
        </div>}
      </div>

      {/* Modals */}
      <AnimatePresence>{showUpload && <motion.div key="m-upload" className={styles.overlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowUpload(false)}>
        <motion.div className={styles.modal} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()}>
          <div className={styles.modalHead}><h2>上传视频</h2><button onClick={()=>setShowUpload(false)}><X size={20}/></button></div>
          <div className={styles.modalBody}>
            <label>标题 *</label><input value={uploadForm.title} onChange={e=>setUploadForm({...uploadForm,title:e.target.value})}/>
            <label>描述</label><textarea value={uploadForm.description} onChange={e=>setUploadForm({...uploadForm,description:e.target.value})} rows={3}/>
            {!isVideoOwner && <><label>级别</label><select value={uploadForm.level} onChange={e=>setUploadForm({...uploadForm,level:Number(e.target.value)})}><option value={1}>1级</option><option value={2}>2级</option><option value={3}>3级</option></select>
            <label>分类</label><select value={uploadForm.category} onChange={e=>setUploadForm({...uploadForm,category:e.target.value})}><option value="">未分类</option>{CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}</select></>}
            <div style={{padding:20,border:'2px dashed var(--ink-200)',borderRadius:8,textAlign:'center',cursor:'pointer',marginTop:8}} onClick={()=>document.getElementById('fupload')?.click()}>
              <input id="fupload" type="file" hidden onChange={e=>{const f=e.target.files?.[0];if(f)setUploadFile(f)}}/>
              {uploadFile?<p>{uploadFile.name} ({formatSize(uploadFile.size)})</p>:<><Upload size={24}/><p>点击选择文件</p></>}
            </div>
            {isVideoOwner&&<p style={{fontSize:'.75rem',color:'var(--ink-400)',marginTop:8}}>视频内容需要符合规范</p>}
          </div>
          <div className={styles.modalFoot}><button onClick={()=>setShowUpload(false)}>取消</button><button className={styles.primary} disabled={uploading||!uploadFile} onClick={handleUpload}>{uploading?'发布中...':'发布'}</button></div>
        </motion.div>
      </motion.div>}</AnimatePresence>

      <AnimatePresence>{showEdit && <motion.div key="m-edit" className={styles.overlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowEdit(false)}>
        <motion.div className={styles.modal} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()}>
          <div className={styles.modalHead}><h2>编辑视频</h2><button onClick={()=>setShowEdit(false)}><X size={20}/></button></div>
          <div className={styles.modalBody}>
            <label>标题</label><input value={editForm.title} onChange={e=>setEditForm({...editForm,title:e.target.value})}/>
            <label>描述</label><textarea value={editForm.description} onChange={e=>setEditForm({...editForm,description:e.target.value})} rows={3}/>
            <label>替换视频文件</label>
            <div style={{padding:16,border:'2px dashed var(--ink-200)',borderRadius:8,textAlign:'center',cursor:'pointer'}} onClick={()=>document.getElementById('editFileInput')?.click()}>
              <input id="editFileInput" type="file" hidden onChange={e=>{const f=e.target.files?.[0];if(f)setEditFile(f);}}/>
              {editFile ? <p>{editFile.name} ({formatSize(editFile.size)})</p> : <p style={{color:'var(--ink-400)',fontSize:'.8125rem'}}>点击选择新文件（不选则不替换）</p>}
            </div>
            {!isVideoOwner && <><label>级别</label><select value={editForm.level} onChange={e=>setEditForm({...editForm,level:Number(e.target.value)})}><option value={1}>1级</option><option value={2}>2级</option><option value={3}>3级</option></select>
            <label>分类</label><select value={editForm.category} onChange={e=>setEditForm({...editForm,category:e.target.value})}><option value="">未分类</option>{CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}</select></>}
          </div>
          <div className={styles.modalFoot}><button onClick={()=>setShowEdit(false)} disabled={saving}>取消</button><button className={styles.primary} onClick={handleSaveEdit} disabled={saving}>{saving ? '保存中...' : '保存'}</button></div>
        </motion.div>
      </motion.div>}</AnimatePresence>

      <AnimatePresence>{showLogs && <motion.div key="m-logs" className={styles.overlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowLogs(false)}>
        <motion.div className={styles.modalLg} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()}>
          <div className={styles.modalHead}><h2>浏览统计 · {logTitle}</h2><button onClick={()=>setShowLogs(false)}><X size={20}/></button></div>
          <div className={styles.modalBody}>
            <table className={styles.logTable}><thead><tr><th style={{textAlign:'left'}}>客户名称</th><th>客户公司</th><th>浏览次数</th><th>浏览时长</th><th>最近浏览</th><th>是否收藏</th></tr></thead>
            <tbody>{viewLogs.length===0?<tr><td colSpan={6} style={{textAlign:'center',padding:20,color:'#9ca3af'}}>暂无浏览记录</td></tr>:viewLogs.map((l,i)=><tr key={i}><td style={{textAlign:'left'}}>{l.viewer_name}</td><td>{l.company||'-'}</td><td>{l.view_count}次</td><td>{formatTime(l.view_duration)}</td><td>{l.last_view?new Date(l.last_view).toLocaleString('zh-CN'):'-'}</td><td>{l.is_liked?'❤️':'-'}</td></tr>)}</tbody></table>
          </div>
        </motion.div>
      </motion.div>}</AnimatePresence>

      <AnimatePresence>{showApproval && <motion.div key="m-approval" className={styles.overlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowApproval(false)}>
        <motion.div className={styles.modal} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()} style={{maxWidth:520}}>
          <div className={styles.modalHead}><h2>审核 · {approvalTitle}</h2><button onClick={()=>setShowApproval(false)}><X size={20}/></button></div>
          <div className={styles.modalBody}>
            {/* 审核记录表格 */}
            <table style={{width:'100%',fontSize:'.8125rem',borderCollapse:'collapse',marginBottom:16}}>
              <thead><tr style={{borderBottom:'2px solid var(--ink-100)'}}>
                <th style={{textAlign:'left',padding:'8px 4px'}}>人物</th>
                <th style={{textAlign:'left',padding:'8px 4px'}}>动作</th>
                <th style={{textAlign:'left',padding:'8px 4px'}}>备注</th>
                <th style={{textAlign:'left',padding:'8px 4px'}}>时间</th>
              </tr></thead>
              <tbody>
                {(() => {
                  const steps = [
                    {role:'采购',label:'规范性审核',s:1},
                    {role:'研发',label:'技术性审核',s:2},
                  ];
                  const rows: Array<{name:string;action:string;comment:string;time:string;color:string}> = [];
                  const logs = [...approvalLogs].sort((a:any,b:any)=>new Date(a.create_time).getTime()-new Date(b.create_time).getTime());

                  // 1. 全部日志按时间排序
                  logs.forEach((l:any)=>{
                    if (l.action==='提交审核') { rows.push({name:l.approver_name,action:'送审',comment:'',time:new Date(l.create_time).toLocaleString('zh-CN'),color:'#16a34a'}); return; }
                    const label = steps.find(s=>s.role===l.approval_role)?.label||l.approval_role;
                    let a=l.action, c='var(--ink-500)';
                    if (l.action==='撤回') { a='撤回审核'; c='#f59e0b'; }
                    else if (l.action==='通过') { a=label+'完成'; c='#16a34a'; }
                    else if (l.action==='驳回') { a=label+'驳回'; c='#dc2626'; }
                    rows.push({name:l.approver_name,action:a,comment:l.comment||'',time:new Date(l.create_time).toLocaleString('zh-CN'),color:c});
                  });

                  // 当前状态（读 approval_status，不推导）
                  if ([1,2,3].includes(approvalStatus)) {
                    steps.forEach(s=>{
                      if (s.s > approvalStatus) rows.push({name:'',action:s.label+'未开始',comment:'',time:'',color:'var(--ink-300)'});
                      else if (s.s === approvalStatus) rows.push({name:'',action:s.label+'中',comment:'',time:'',color:'#2563eb'});
                    });
                  }
                  if (approvalStatus===4) rows.push({name:'',action:'已上线',comment:'',time:'',color:'#16a34a'});
                  if (approvalStatus===0 && rows.length===0) rows.push({name:'',action:'未送审',comment:'',time:'',color:'var(--ink-300)'});

                  return rows.map((r,i)=><tr key={i} style={{borderBottom:'1px solid #f5f5f5'}}>
                    <td style={{padding:'6px 4px'}}>{r.name||'-'}</td>
                    <td style={{padding:'6px 4px',color:r.color,fontWeight:500}}>{r.action}</td>
                    <td style={{padding:'6px 4px',color:'var(--ink-400)'}}>{r.comment||'-'}</td>
                    <td style={{padding:'6px 4px',color:'var(--ink-400)'}}>{r.time||'-'}</td>
                  </tr>);
                })()}
              </tbody>
            </table>
            {/* 操作按钮 */}
            {[0,5].includes(approvalStatus) && <button onClick={handleSubmitApproval} style={{width:'100%',padding:'10px',borderRadius:8,background:'var(--cinnabar-500)',color:'#fff',border:'none',cursor:'pointer'}}>{approvalStatus===5?'重新送审':'送审'}</button>}
            {[1,2,3].includes(approvalStatus) && <button onClick={handleWithdraw} style={{width:'100%',padding:'10px',borderRadius:8,background:'#fefce8',border:'1px solid #fbbf24',color:'#b45309',cursor:'pointer'}}>撤回</button>}
            {approvalStatus===4 && <div style={{padding:'10px',borderRadius:8,background:'#dcfce7',color:'#16a34a',textAlign:'center'}}>已发布</div>}
          </div>
        </motion.div>
      </motion.div>}</AnimatePresence>

      {/* 研发发布分类弹窗 */}
      <AnimatePresence>{showUploadCategory && <motion.div key="m-upload-cat" className={styles.overlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowUploadCategory(false)}>
        <motion.div className={styles.modal} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()} style={{maxWidth:380}}>
          <div className={styles.modalHead}><h2>上传视频 · {approvalTitle}</h2><button onClick={()=>setShowUploadCategory(false)}><X size={20}/></button></div>
          <div className={styles.modalBody}>
            <label style={{fontSize:'.875rem',fontWeight:500,marginBottom:8,display:'block'}}>视频分类</label>
            <select value={uploadCategory} onChange={e=>setUploadCategory(e.target.value)}
              style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1px solid var(--ink-200)',fontSize:'.875rem'}}>
              <option value="">请选择分类</option>
              {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            <div style={{marginTop:16,display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={()=>setShowUploadCategory(false)} style={{padding:'8px 20px',borderRadius:8,border:'1px solid var(--ink-200)',background:'#fff',cursor:'pointer',fontSize:'.875rem'}}>取消</button>
              <button onClick={async ()=>{
                if (!uploadCategory) { showToast('warning','请选择分类'); return; }
                try {
                  await api.updatePromotionFile(approvalFileId, { category: uploadCategory, approval_status: 4 } as any);
                  showToast('success','视频已上架'); setShowUploadCategory(false); setUploadCategory(''); loadData();
                } catch(err) { showToast('error', getErrorMessage(err)); }
              }} style={{padding:'8px 20px',borderRadius:8,border:'none',background:'var(--cinnabar-500)',color:'#fff',cursor:'pointer',fontSize:'.875rem'}}>确认上架</button>
            </div>
          </div>
        </motion.div>
      </motion.div>}</AnimatePresence>

      {/* 点赞弹窗 */}
      <AnimatePresence>{showLikeDetail && <motion.div key="m-like" className={styles.overlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowLikeDetail(false)}>
        <motion.div className={styles.modal} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()} style={{maxWidth:380}}>
          <div className={styles.modalHead}><h2>❤️ 点赞 · {detailTitle}</h2><button onClick={()=>setShowLikeDetail(false)}><X size={20}/></button></div>
          <div className={styles.modalBody}>
            <p style={{fontSize:'.875rem',fontWeight:600,marginBottom:12}}>{detailLikes[0]?.count||0} 人赞了</p>
            <p style={{fontSize:'.75rem',color:'var(--ink-400)'}}>客户端上线后将展示点赞用户列表</p>
          </div>
        </motion.div>
      </motion.div>}</AnimatePresence>

      {/* 评论弹窗 */}
      <AnimatePresence>{showCommentModal && <motion.div key="m-comments" style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:24}} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowCommentModal(false)}>
        <motion.div style={{width:'100%',maxWidth:560,maxHeight:'85vh',background:'#fff',borderRadius:14,display:'flex',flexDirection:'column'}} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()}>
          <div style={{padding:'14px 20px',fontWeight:600,borderBottom:'1px solid var(--ink-100)',display:'flex',justifyContent:'space-between'}}>
            <span>💬 评论 · {commentModalTitle}</span>
            <button onClick={()=>setShowCommentModal(false)} style={{background:'none',border:'none',cursor:'pointer'}}><X size={18}/></button>
          </div>
          <div style={{flex:1,overflow:'auto',padding:'8px 20px'}}>
            {(cardComments[commentModalFileId]||[]).length === 0 ? (
              <div style={{textAlign:'center',padding:40,color:'var(--ink-300)',fontSize:'.8125rem'}}>暂无评论</div>
            ) : (
              (cardComments[commentModalFileId]||[]).map((c:any) => (
                <div key={c.id} style={{padding:'14px 0',borderBottom:'1px solid #f5f5f5'}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                    <span style={{fontSize:'.8125rem',fontWeight:500,color:'#8b8b8b'}}>{c.user_name}</span>
                    <span style={{fontSize:'.6875rem',color:'#bbb'}}>{new Date(c.create_time).toLocaleString('zh-CN')}</span>
                  </div>
                  <div style={{fontSize:'.875rem',color:'#222',lineHeight:1.6,wordBreak:'break-word'}}>{c.content}</div>
                  {c.reply && (
                    <div style={{marginTop:10,padding:'10px 14px',background:'#f8f8f8',borderRadius:8,fontSize:'.8125rem'}}>
                      <span style={{fontWeight:500,color:'#8b8b8b'}}>回复：</span>
                      <span style={{color:'#555'}}>{c.reply}</span>
                    </div>
                  )}
                  <div style={{textAlign:'right',marginTop:6}}>
                    <button onClick={() => setReplyTo(replyTo===c.id ? null : c.id)} style={{background:'none',border:'none',color:'#8b8b8b',fontSize:'.75rem',cursor:'pointer',padding:'2px 8px'}}>回复</button>
                  </div>
                  {replyTo === c.id && (
                    <div style={{display:'flex',gap:8,marginTop:8}}>
                      <input value={replyText} onChange={e=>setReplyText(e.target.value)} placeholder={`回复 ${c.user_name}...`}
                        style={{flex:1,padding:'8px 12px',borderRadius:8,border:'1px solid #e5e5e5',fontSize:'.8125rem',outline:'none'}}
                        onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault(); handleReply(c.id, commentModalFileId);}}} />
                      <button onClick={()=>handleReply(c.id, commentModalFileId)} disabled={!replyText.trim() || replySending}
                        style={{padding:'6px 14px',borderRadius:8,border:'none',background:'var(--cinnabar-500)',color:'#fff',cursor:'pointer',fontSize:'.75rem',opacity:replyText.trim()?1:.5}}>发送</button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </motion.div>
      </motion.div>}</AnimatePresence>

      {/* 收藏弹窗 */}
      <AnimatePresence>{showFavDetail && <motion.div key="m-fav" className={styles.overlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowFavDetail(false)}>
        <motion.div className={styles.modal} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()} style={{maxWidth:380}}>
          <div className={styles.modalHead}><h2>⭐ 收藏 · {detailTitle}</h2><button onClick={()=>setShowFavDetail(false)}><X size={20}/></button></div>
          <div className={styles.modalBody}>
            <p style={{fontSize:'.875rem',fontWeight:600,marginBottom:12}}>0 人收藏</p>
            <p style={{fontSize:'.75rem',color:'var(--ink-400)'}}>客户端上线后将展示收藏用户列表</p>
          </div>
        </motion.div>
      </motion.div>}</AnimatePresence>

      {/* 评论弹窗 */}
      <AnimatePresence>{showCommentDetail && <motion.div key="m-comment" className={styles.overlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowCommentDetail(false)}>
        <motion.div className={styles.modal} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()} style={{maxWidth:440}}>
          <div className={styles.modalHead}><h2>💬 评论 · {detailTitle}</h2><button onClick={()=>setShowCommentDetail(false)}><X size={20}/></button></div>
          <div className={styles.modalBody}>
            {detailComments.length===0 ? <p style={{fontSize:'.75rem',color:'var(--ink-400)',textAlign:'center',padding:20}}>暂无评论</p> :
            detailComments.map((c:any)=><div key={c.id} style={{padding:'12px 0',borderBottom:'1px solid #f5f5f5'}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                <span style={{fontSize:'.8125rem',fontWeight:500,color:'#8b8b8b'}}>{c.user_name}</span>
                <span style={{fontSize:'.6875rem',color:'#bbb'}}>{new Date(c.create_time).toLocaleString('zh-CN')}</span>
              </div>
              <div style={{fontSize:'.875rem',color:'#222',lineHeight:1.6}}>{c.content}</div>
              {c.reply && <div style={{marginTop:8,padding:'8px 12px',background:'#f8f8f8',borderRadius:8,fontSize:'.8125rem'}}><span style={{fontWeight:500,color:'#8b8b8b'}}>回复：</span><span style={{color:'#555'}}>{c.reply}</span></div>}
            </div>)}
          </div>
        </motion.div>
      </motion.div>}</AnimatePresence>

      <AnimatePresence>{showMsg && <motion.div key="m-msg" ref={msgRef} initial={{opacity:0,y:-8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-8}} style={{position:'fixed',top:60,right:16,width:360,maxHeight:480,overflow:'auto',background:'#fff',borderRadius:12,boxShadow:'0 10px 40px rgba(0,0,0,.15)',zIndex:200,padding:'12px 18px'}}>
        <div style={{fontWeight:600,fontSize:'.875rem',borderBottom:'1px solid #eee',paddingBottom:8,marginBottom:8}}>消息中心</div>
        {messages.length===0?<div style={{textAlign:'center',color:'#9ca3af',padding:20}}>暂无消息</div>:messages.map((m:any,i:number)=><div key={i} style={{padding:'8px 0',borderBottom:'1px solid #f5f5f5',fontSize:'.8125rem',display:'flex',gap:8}}>{m.type==='like'?<Heart size={14} style={{color:'#dc2626'}}/>:<MessageSquare size={14} style={{color:'#2563eb'}}/>}<div><strong>{m.user_name}</strong>{m.type==='like'?'赞了':'评论了'}<strong>{m.title}</strong>{m.content&&<div style={{color:'#999',marginTop:2}}>{m.content}</div>}</div></div>)}
      </motion.div>}</AnimatePresence>

      <AnimatePresence>{previewFile && <motion.div key="m-preview" className={styles.overlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setPreviewFile(null)}>
        <motion.div style={{maxWidth:'90vw',maxHeight:'90vh',background:'#000',borderRadius:12,overflow:'hidden'}} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()}>
          <div style={{display:'flex',justifyContent:'flex-end',padding:'8px 16px',background:'#1a1a1a'}}><button onClick={()=>setPreviewFile(null)} style={{background:'none',border:'none',color:'#fff',cursor:'pointer'}}><X size={20}/></button></div>
          {['mp4','webm','mov','avi'].includes(previewFile.file_type?.toLowerCase()||'')?<video src={previewFile.preview_url} controls autoPlay playsInline controlsList="nodownload" onContextMenu={e=>e.preventDefault()} style={{maxWidth:'90vw',maxHeight:'70vh'}}/>:<img src={previewFile.preview_url?.replace('/minio/','/internal-minio/')} style={{maxWidth:'90vw',maxHeight:'70vh'}}/>}
        </motion.div>
      </motion.div>}</AnimatePresence>
      <div style={{position:'fixed',bottom:28,zIndex:100,left:currentFunctionRole==='供应商'?'50%':'calc(50% + 130px)',transform:'translateX(-50%)'}}>
        <motion.button onClick={() => setShowUpload(true)} whileHover={{scale:1.1}} whileTap={{scale:.9}}
          style={{width:64,height:64,borderRadius:'50%',background:'var(--cinnabar-500)',color:'white',border:'none',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',boxShadow:'0 4px 24px rgba(197,48,48,.4)'}}>
          <Plus size={28} strokeWidth={2} />
        </motion.button>
      </div>
    </div>
  );
}
